import { useState, useEffect, useCallback } from 'react';
import { NotificationService, type NotificationPreferences } from '../lib/notification-service';
import { useToast } from '../components/ui/use-toast';

export const useNotifications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const { toast } = useToast();

  // Initialize notification service
  useEffect(() => {
    const initNotifications = async () => {
      // Check if notifications are supported
      setIsSupported('Notification' in window && 'serviceWorker' in navigator);
      
      if ('Notification' in window) {
        setHasPermission(Notification.permission === 'granted');
      }

      // Load user preferences
      try {
        const notificationService = new NotificationService();
        const userPrefs = await notificationService.getNotificationPreferences();
        setPreferences(userPrefs);
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      }
    };

    initNotifications();
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast({
        title: "Not Supported",
        description: "Push notifications are not supported in this browser.",
        variant: "destructive",
      });
      return false;
    }

    setIsLoading(true);
    try {
      const notificationService = new NotificationService();
      const permission = await notificationService.requestPermission();
      
      if (permission === 'granted') {
        setHasPermission(true);
        // Subscribe to push notifications
        await notificationService.subscribeToPush();
        
        toast({
          title: "Notifications Enabled",
          description: "You'll now receive notifications about expiring food items.",
          duration: 3000,
        });
        return true;
      } else {
        toast({
          title: "Permission Denied",
          description: "Please enable notifications in your browser settings to receive alerts.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      toast({
        title: "Error",
        description: "Failed to set up notifications. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, toast]);

  const updatePreferences = useCallback(async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return;

    setIsLoading(true);
    try {
      const notificationService = new NotificationService();
      const success = await notificationService.updateNotificationPreferences({
        ...preferences,
        ...updates,
      });
      
      if (success) {
        // Update local state with the changes
        setPreferences(prev => prev ? { ...prev, ...updates } : null);
        
        toast({
          title: "Settings Updated",
          description: "Your notification preferences have been saved.",
          duration: 2000,
        });
      } else {
        throw new Error('Failed to update preferences');
      }
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update notification settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [preferences, toast]);

  const toggleExpiryNotifications = useCallback(async (enabled: boolean) => {
    if (enabled && !hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }

    await updatePreferences({ expiry_notifications_enabled: enabled });
  }, [hasPermission, requestPermission, updatePreferences]);

  const toggleDailySummary = useCallback(async (enabled: boolean) => {
    if (enabled && !hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }

    await updatePreferences({ daily_summary_enabled: enabled });
  }, [hasPermission, requestPermission, updatePreferences]);

  const checkExpiringItems = useCallback(async () => {
    if (!preferences?.expiry_notifications_enabled || !hasPermission) {
      return;
    }

    try {
      const notificationService = new NotificationService();
      await notificationService.checkAndNotifyExpiringItems();
    } catch (error) {
      console.error('Failed to check expiring items:', error);
    }
  }, [preferences?.expiry_notifications_enabled, hasPermission]);

  const testNotification = useCallback(async () => {
    if (!hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }

    try {
      const notificationService = new NotificationService();
      await notificationService.showLocalNotification({
        type: 'expiry_today',
        title: 'Test Notification',
        body: 'This is a test notification from Nosh!',
        icon: '/nosh-new-icon.png',
      });
      
      toast({
        title: "Test Sent",
        description: "Check your notifications to see if it worked!",
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast({
        title: "Test Failed",
        description: "Could not send test notification.",
        variant: "destructive",
      });
    }
  }, [hasPermission, requestPermission, toast]);

  return {
    isLoading,
    hasPermission,
    preferences,
    isSupported,
    requestPermission,
    updatePreferences,
    toggleExpiryNotifications,
    toggleDailySummary,
    checkExpiringItems,
    testNotification,
  };
}; 